/**
 * The only place fetch() is called.
 *
 * Server errors arrive as { error: { code, message } }; ApiError preserves the
 * code so a caller can branch (COOLDOWN vs NOT_ON_DISPLAY) while still having a
 * human-readable message ready to show.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public detail: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

/**
 * How long a request may hang before the UI gives up on it.
 *
 * `fetch` has no timeout of its own: a request to a server that has accepted
 * the connection and then stalled never settles, and every screen in this app
 * renders a spinner until its promise settles. That is the whole mechanism
 * behind "pages get stuck loading" — the pages were not broken, they were
 * waiting on something that was never going to answer. The server-side stalls
 * that caused it are fixed, but a client that can wait forever is a client that
 * will eventually be asked to, so it now stops and says so.
 *
 * Writes get longer than reads because a few of them legitimately take a while
 * — hatching twenty eggs does real work — and a write that is abandoned while
 * it is still committing is the one case where giving up early is worse than
 * waiting. Every request still ends in success or an error, never in silence.
 */
const READ_TIMEOUT_MS = 15_000;
const WRITE_TIMEOUT_MS = 30_000;

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

async function request<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const isRead = method === 'GET' || method === 'HEAD';
  const controller = new AbortController();
  const timer = window.setTimeout(
    () => controller.abort(),
    isRead ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS,
  );

  let response: Response;
  try {
    response = await fetch(withQuery(`/api${path}`, query), {
      method,
      credentials: 'same-origin',
      signal: controller.signal,
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (caught) {
    // A timeout and a dead connection need different words: one is "the game is
    // busy, try again", the other is "check your wifi", and telling somebody to
    // check a working connection is its own kind of broken.
    if (caught instanceof DOMException && caught.name === 'AbortError') {
      throw new ApiError(
        0,
        'TIMEOUT',
        isRead
          ? 'PokéGrove took too long to answer. Try again in a moment.'
          : "That took too long — it may still have gone through. Refresh before trying again.",
      );
    }
    throw new ApiError(0, 'OFFLINE', "Can't reach PokeGrove. Check your connection.");
  } finally {
    window.clearTimeout(timer);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: Record<string, unknown> } | null)?.error;
    throw new ApiError(
      response.status,
      String(error?.code ?? 'ERROR'),
      String(error?.message ?? 'Something went wrong.'),
      error ?? {},
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, query?: Query) => request<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body ?? {}),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body ?? {}),
  /** `del` rather than `delete`, which is a reserved word in an object literal. */
  del: <T>(path: string) => request<T>('DELETE', path),
};
